import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {SkillGroupBaseModel} from '../../../models/edu-admin/skillGroupBase.model';
import {SkillGroupStructureModel} from '../../../models/student/skillGroupStatusModel';
import {StudentModel} from '../../../models/authentication/student.model';
import {AppEventsService, AppEventType, ScrollEvent} from '../../../services/events/app-events.service';
import {SchoolGroupsService} from '../../../services/school/school-groups.service';
import {TranslateService} from '@ngx-translate/core';
import {QuestionLiteModel} from '../../../models/questions/question-lite.model';

@Component({
  selector: 'app-evo-student-skill-grid-view',
  templateUrl: './evo-student-skill-grid-view.component.html',
  styleUrls: ['./evo-student-skill-grid-view.component.css']
})
export class EvoStudentSkillGridViewComponent implements OnInit {

  containerWidth = 0;
  containerWidthString = '0px';

  private _students: StudentModel[] = [];
  @Input('students')
  get students(): StudentModel[] {
    return this._students;
  }

  set students(value: StudentModel[]) {
    this._students = value;
    this.containerWidth = value.length * 62;
    this.containerWidthString = this.containerWidth.toString(10) + 'px';
  }

  @Input() skillGroups: SkillGroupBaseModel[] = [];
  @Input() skillGroupsStructure: SkillGroupStructureModel;

  @Input() invertAxes = false;
  @Input() skillGroupExpandEnabled = false;

  @ViewChild('skillHeader', { static: false }) skillHeader: ElementRef;
  @ViewChild('skillContainer', { static: true }) skillContainer: ElementRef;

  topOffset = '0px';

  selectedSkillGroup: SkillGroupBaseModel | null = null;
  questionCache: Map<string, QuestionLiteModel> = new Map();
  loadingSkillGroupId: string | null = null;

  constructor(public appEventSv: AppEventsService,
              public tSv: TranslateService,
              private schoolGroupsSv: SchoolGroupsService) { }

  ngOnInit() {
    // Listen for specific scroll events from the group dashboard
    this.appEventSv.onScrollEvent('group-dashboard').subscribe((event) => {
      this.positionHeaderOnScroll(event);
    });
  }

  positionHeaderOnScroll(scrollEvent: ScrollEvent) {

    const scrollDiv = scrollEvent.container;

    if (scrollDiv != null && this.skillContainer != null && this.skillHeader != null) {
      // start location of the scroll container
      const startY = scrollDiv.offsetTop;

      // current y location with the scroll
      const currentY = startY + scrollDiv.scrollTop;

      // start position to move header
      const yDiff = currentY - this.skillContainer.nativeElement.offsetTop;

      // Need to subtract scroll bar height if any
      const stopHeight = this.skillContainer.nativeElement.offsetHeight - this.skillHeader.nativeElement.offsetHeight;

      if (yDiff >= 0 && yDiff < stopHeight) {
        this.topOffset = (yDiff + 2).toString() + 'px';
      } else if (yDiff < 0) {
        this.topOffset = '0px';
      }
    } else {
      console.warn('positionHeaderOnScroll: Missing required elements:', {
        scrollDiv: !!scrollDiv,
        skillContainer: !!this.skillContainer,
        skillHeader: !!this.skillHeader
      });
    }
  }


  toggleSkillGroup(skillGroup: SkillGroupBaseModel) {
    if (!this.skillGroupExpandEnabled) { return; }
    if (this.selectedSkillGroup && this.selectedSkillGroup._id === skillGroup._id) {
      this.selectedSkillGroup = null;
      return;
    }
    this.selectedSkillGroup = skillGroup;
    const missingIds = skillGroup.questionIds.filter(id => !this.questionCache.has(id));
    if (missingIds.length > 0) {
      this.loadingSkillGroupId = skillGroup._id;
      this.schoolGroupsSv.fetchQuestionsForTeacher(missingIds).subscribe(questions => {
        questions.forEach(q => this.questionCache.set(q._id, q));
        this.loadingSkillGroupId = null;
      });
    }
  }

  isSkillGroupSelected(skillGroup: SkillGroupBaseModel): boolean {
    return this.skillGroupExpandEnabled && this.selectedSkillGroup !== null && this.selectedSkillGroup._id === skillGroup._id;
  }

  isLoadingSkillGroup(skillGroup: SkillGroupBaseModel): boolean {
    return this.loadingSkillGroupId === skillGroup._id;
  }

  getQuestion(questionId: string): QuestionLiteModel | null {
    return this.questionCache.get(questionId) || null;
  }

  colorForQuestion(studentId: string, skillGroupId: string, questionId: string): { 'background-color': string, 'border-width': string, 'border-style': string, 'border-color': string } {

    if (!this.skillGroupsStructure) {
      return this.grayColor();
    }

    const groupStatus = this.skillGroupsStructure.skillGroupStatusForStudent(studentId, skillGroupId);
    if (!groupStatus) {
      return this.grayColor();
    }

    const skillStatus = groupStatus.skillStatuses.find(s => s.questionId === questionId);
    if (studentId === '6a956dde4a94e300324f04e0' && questionId === '67408b2265b50a0025ecf58c') {
      console.log('found student id and question id for skill status: ' + JSON.stringify(skillStatus, null, 4));
    }
    if (!skillStatus) {
      return this.grayColor();
    }

    const criterionForProblemArea = groupStatus.type === 'placement' ? 2 : 3;
    if (skillStatus.numWrongConsecutively >= criterionForProblemArea) {
      return { 'background-color': 'rgba(215, 33, 33, 0.3)', 'border-width': '1px', 'border-style': 'solid', 'border-color': 'rgba(215, 33, 33, 1.0)' };
    }

    const value = skillStatus.mastery;
    if (value >= 0 && value < 0.5 && skillStatus.numAnswers() > 0) {
      return { 'background-color': 'rgba(255, 203, 5, 0.3)', 'border-width': '1px', 'border-style': 'solid', 'border-color': 'rgba(255, 203, 5, 1.0)' };
    } else if (value >= 0.5 && value < 0.8) {
      return { 'background-color': 'rgba(155, 89, 182, 0.3)', 'border-width': '1px', 'border-style': 'solid', 'border-color': 'rgba(155, 89, 182, 1.0)' };
    } else if (value >= 0.8 && value < 1.0) {
      return { 'background-color': 'rgba(121, 197, 112, 0.3)', 'border-width': '1px', 'border-style': 'solid', 'border-color': 'rgba(121, 197, 112, 1.0)' };
    } else if (value >= 1.0) {
      return { 'background-color': 'rgba(0, 156, 204, 0.3)', 'border-width': '1px', 'border-style': 'solid', 'border-color': 'rgba(0, 156, 204, 1.0)' };
    }

    return this.grayColor();

  }

  private grayColor() {
    return { 'background-color': 'rgba(216, 216, 216, 0.3)', 'border-width': '1px', 'border-style': 'solid', 'border-color': 'rgba(216, 216, 216, 1)' };
  }

  colorForMastery(studentId: string, skillGroupId: string): { 'background-color': string, 'border-width': string, 'border-style': string, 'border-color': string } {

    let value = null;

    if (this.skillGroupsStructure) {
      value = this.skillGroupsStructure.masteryForStudentSkillGroupStatus(studentId, skillGroupId);
    }

    if (value === -2) {
      // Indicates the student is struggling with a question/skill in the skill group
      return {
        'background-color': 'rgba(215, 33, 33, 0.3)',
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': 'rgba(215, 33, 33, 1.0)'
      };

    } else if (value >= 0 && value < 0.5 && this.skillGroupsStructure.numAnswersForStudentSkillGroupStatus(studentId, skillGroupId) > 0) {

      return {
        'background-color': 'rgba(255, 203, 5, 0.3)',
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': 'rgba(255, 203, 5, 1.0)'
      };

    } else if (value >= 0.5 && value < 0.8) {


      return {
        'background-color': 'rgba(155, 89, 182, 0.3)',
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': 'rgba(155, 89, 182, 1.0)'
      };


    } else if (value >= 0.8 && value < 1.0) {

      return {
        'background-color': 'rgba(121, 197, 112, 0.3)',
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': 'rgba(121, 197, 112, 1.0)'
      };

    } else if (value >= 1.0) {

      return {
        'background-color': 'rgba(0, 156, 204, 0.3)',
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': 'rgba(0, 156, 204, 1.0)'
      };

    }

    return this.grayColor();

  }

}
